import {
  Popover as HUIPopover,
  PopoverButton as HUIPopoverButton,
  PopoverButtonProps as HUIPopoverButtonProps,
  PopoverPanel as HUIPopoverPanel,
} from '@headlessui/react';
import { ReactNode, useState, ReactElement } from 'react';
import type { ElementType, HTMLAttributes } from 'react';
import { usePopper } from 'react-popper';
import type { Options } from '@popperjs/core';
import { Arrow, ArrowPositioner, HUIPopoverWrapper } from './Popover.styles';
import { ContentWrapper } from './ContentWrapper';

type ButtonSize = 'small' | 'large';

type ButtonCommonProps = {
  icon?: ReactElement;
  iconAlignment?: 'left' | 'right';
  size?: ButtonSize;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  textId?: string;
};

export interface PopoverProps extends HTMLAttributes<HTMLElement> {
  button: HUIPopoverButtonProps<ElementType> & ButtonCommonProps;
  position?: 'top' | 'bottom' | 'left' | 'right';
  children?: ReactNode;
}

const Popover = ({ button, position = 'bottom', children }: PopoverProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  const options: Options = {
    placement: position,
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
    strategy: 'absolute',
  };
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(
    referenceElement,
    popperElement,
    options,
  );

  return (
    <HUIPopoverWrapper position={position}>
      <HUIPopover>
        <HUIPopoverButton
          {...button}
          role={button.as !== 'button' ? 'button' : undefined}
          ref={setReferenceElement}
        />
        <HUIPopoverPanel
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <ContentWrapper>{children}</ContentWrapper>
          <ArrowPositioner
            ref={setArrowElement}
            style={styles.arrow}
            position={position}
          >
            <Arrow position={position} />
          </ArrowPositioner>
        </HUIPopoverPanel>
      </HUIPopover>
    </HUIPopoverWrapper>
  );
};

export { Popover };
